import { useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/** Gap between the target and the cut-out's edge; clears the target's focus ring. */
const PADDING = 6;

interface TourSpotlightProps {
  /** Left undimmed; with none, the whole screen dims. */
  target: HTMLElement | null;
  /** A press anywhere on the screen, which the tour holds still while it runs. */
  onBlockedClick: () => void;
}

/**
 * Dims the screen behind a tour step, leaving an outlined cut-out over its target.
 * Takes every press, the target's included, as the tour is click-through only.
 * Sits under the step's card, so its buttons stay usable.
 */
export function TourSpotlight({ target, onBlockedClick }: TourSpotlightProps) {
  const hole = useRef<HTMLDivElement>(null);

  // Followed every frame, as the target can move with panels, scrolling and resizes.
  useLayoutEffect(() => {
    const element = hole.current;
    if (!target || !element) return;
    let frame: number;
    let last = '';
    const track = () => {
      const { left, top, width, height } = target.getBoundingClientRect();
      const next = `${left},${top},${width},${height}`;
      if (next !== last) {
        last = next;
        element.style.left = `${left - PADDING}px`;
        element.style.top = `${top - PADDING}px`;
        element.style.width = `${width + PADDING * 2}px`;
        element.style.height = `${height + PADDING * 2}px`;
      }
      frame = requestAnimationFrame(track);
    };
    track();
    return () => cancelAnimationFrame(frame);
  }, [target]);

  return createPortal(
    <div
      aria-hidden
      data-testid="tour-spotlight"
      className="fixed inset-0 z-50 overflow-hidden"
      onPointerDown={onBlockedClick}
    >
      {target ? (
        <div
          ref={hole}
          className="absolute rounded-md shadow-[0_0_0_200vmax_rgba(0,0,0,0.5)] outline outline-2 outline-explorer-marker"
        />
      ) : (
        <div className="absolute inset-0 bg-black/50" />
      )}
    </div>,
    document.body
  );
}
